import { useMemo } from 'react';

import { Grid, Box } from '@mui/material';
import GlobalLoader from '@apigrowfinance/pbolt-global-loader';

import CardLinkPanel from '@components/overviewPanels/cardLinkPanel/CardLinkPanel';
import CreditLimitPanelContainer from '@components/overviewPanels/creditLimitPanel/CreditLimitPanelContainer';
import CurrentBalanceContainer from '@components/overviewPanels/currentBalance/CurrentBalanceContainer';
import DelinquencyPanelContainer from '@components/overviewPanels/delinquencyPanel/DelinquncyPanelContainer';
import DisbursementsPanel from '@components/overviewPanels/disbursementsPanel/DisbursementsPanel';
import LoanPanel from '@components/overviewPanels/loanPanel/LoanPanel';
import LoanRepaymentsPanel from '@components/overviewPanels/loanRepaymentsPanel/LoanRepaymentsPanel';
import MapPanelContainer from '@components/overviewPanels/mapPanel/MapPanelContainer';
import OverviewItem, {
    panelItem,
} from '@components/overviewPanels/OverviewItem';
import RepaymentsSchedulePanel from '@components/overviewPanels/repaymentsSchedulePanel/RepaymentsSchedulePanel';
import StatementsPanelContainer from '@components/overviewPanels/statementPanel/StatementsPanelContainer';
import WithdrawalLimitPieContainer from '@components/overviewPanels/withdrawalLimitPiePanel/WithdrawalLimitPieContainer';
import { useAccount } from '@contexts/AccountProvider';
import TimelinePanelContainer from '@pages/accountTimelinePanel/TimelinePanelContainer';

const Overview = () => {
    const { account, isLoading } = useAccount();

    const creditCardPanels: panelItem[] = useMemo(
        () => [
            {
                name: CurrentBalanceContainer.name,
                component: <CurrentBalanceContainer />,
                grid: { xs: 12, md: 6 },
            },
            {
                name: DelinquencyPanelContainer.name,
                component: (
                    <DelinquencyPanelContainer>
                        <CardLinkPanel />
                    </DelinquencyPanelContainer>
                ),
                grid: { xs: 12, md: 6 },
            },
            {
                name: TimelinePanelContainer.name,
                component: <TimelinePanelContainer />,
                grid: { xs: 12, md: 6 },
            },
            {
                name: StatementsPanelContainer.name,
                component: <StatementsPanelContainer />,
                grid: { xs: 12, md: 6 },
            },
            {
                name: CreditLimitPanelContainer.name,
                component: <CreditLimitPanelContainer />,
                grid: { xs: 12, sm: 12, md: 12, lg: 6 },
            },
            {
                name: WithdrawalLimitPieContainer.name,
                component: <WithdrawalLimitPieContainer />,
                grid: { xs: 12, md: 6, lg: 3 },
            },
            {
                name: MapPanelContainer.name,
                component: <MapPanelContainer />,
                grid: { xs: 12, md: 6, lg: 3 },
            },
        ],
        [],
    );

    const debitCardPanels: panelItem[] = useMemo(
        () => [
            {
                name: CurrentBalanceContainer.name,
                component: <CurrentBalanceContainer />,
                grid: { xs: 12, md: 6 },
            },
            {
                name: CardLinkPanel.name,
                component: <CardLinkPanel />,
                grid: { xs: 12, md: 6 },
            },
            {
                name: TimelinePanelContainer.name,
                component: <TimelinePanelContainer />,
                grid: { xs: 12, md: 6 },
            },
            {
                name: MapPanelContainer.name,
                component: <MapPanelContainer />,
                grid: { xs: 12, md: 6, lg: 6 },
            },
        ],
        [],
    );

    const lendingPanels: panelItem[] = useMemo(
        () => [
            {
                name: LoanPanel.name,
                component: <LoanPanel />,
                grid: { xs: 12, md: 6 },
                minHeight: '351px',
            },
            {
                name: LoanRepaymentsPanel.name,
                component: <LoanRepaymentsPanel />,
                grid: { xs: 12, md: 6 },
            },
            {
                name: DisbursementsPanel.name,
                component: <DisbursementsPanel />,
                grid: { xs: 12, md: 6 },
            },
            {
                name: RepaymentsSchedulePanel.name,
                component: <RepaymentsSchedulePanel />,
                grid: { xs: 12, md: 6 },
            },
        ],
        [],
    );

    const panelsToDisplay = useMemo(() => {
        switch (account?.program_type) {
            case 'LENDING':
                return lendingPanels;
            case 'CREDITO':
                return creditCardPanels;
            case 'DEBITO':
            default:
                return debitCardPanels;
        }
    }, [
        account?.program_type,
        creditCardPanels,
        debitCardPanels,
        lendingPanels,
    ]);

    if (isLoading) {
        return (
            <Box
                data-testid="account-program-type-loading-spinner"
                display="flex"
                m="auto"
                justifyContent="center"
                alignContent="center">
                <GlobalLoader />
            </Box>
        );
    }

    return (
        <Grid container spacing={2}>
            {panelsToDisplay.map((panel, index) => (
                <OverviewItem key={index} {...panel} />
            ))}
        </Grid>
    );
};

export default Overview;
